
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseSection from "@/components/base/BaseSection.vue";
import useAuth from "@/composables/useAuth";
import { CouponResponse } from "@/models/api/CouponResponse";
import discountService from "@/services/discount.service";
import { useCampaignStore } from "@/store/campaign";
import { useUserStore } from "@/store/user";
import { defineComponent, reactive, ref } from "vue";
import useVuelidate from "@vuelidate/core";

export default defineComponent({
	components: {
		BaseSection,
		BaseInput,
		BaseButton,
		BaseAlert,
	},
	props: {
		applyVoucher: {
			type: Boolean,
			default: true
		},
	},
	setup(props) {
		const { getIsLoggedIn } = useUserStore();
		const { reAuthenticate } = useAuth();
		const store = useCampaignStore();

		const loading = ref(false);
		const error = ref<string>("");
		const data = ref(
			store.getState.name
				? {
						campaign: store.getState.name,
				  }
				: undefined
		);

		const state = reactive({
			code: store.getState.code,
		});

		const rules = {
			code: {
				v: () => !state.code || state.code.length > 3,
			},
		};

		const v$ = useVuelidate(rules, state);

		const submit = async () => {
			const valid = await v$.value.$validate();
			if (valid && state.code.length > 0) {
				loading.value = true;
				const applyDiscount = (code: string): Promise<void> => {
					const fn = () => {
						return discountService.apply(code, props.applyVoucher);
					};

					const handleResponse = (resp: CouponResponse) => {
						data.value = resp;
						store.setState({
							code: state.code,
							name: resp.campaign,
						});
					};

					return new Promise((resolve, reject) => {
						if (!getIsLoggedIn) {
							reAuthenticate<CouponResponse>(fn)
								.then((resp) => {
									handleResponse(resp);
									resolve();
								})
								.catch((err) => {
									reject(err);
								});
						} else {
							fn()
								.then((resp) => {
									handleResponse(resp);
									resolve();
								})
								.catch((err) => {
									reject(err);
								});
						}
					});
				};

				applyDiscount(state.code).then(() => {
					error.value = "";
				})
				.catch(() => (error.value = "Invalid code"))
				.finally(() => (loading.value = false));
			}
		};

		const remove = () => {
			loading.value = true;
			discountService
				.remove()
				.then((resp) => {
					data.value = undefined;
					store.setState({
						code: state.code,
						name: "",
					});
				})
				.finally(() => (loading.value = false));
		};

		return { submit, v$, data, error, remove, loading };
	},
});
